import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Package, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ProductBundleManagerProps {
  productId: string;
  onBundlesChange?: () => void;
}

const ProductBundleManager = ({ productId, onBundlesChange }: ProductBundleManagerProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newBundle, setNewBundle] = useState({
    bundle_product_id: '',
    quantity: 1,
    discount_percentage: 0
  });

  // Fetch available products
  const { data: availableProducts } = useQuery({
    queryKey: ['available-products', productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, price, image_url')
        .neq('id', productId)
        .eq('status', 'active');
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch existing bundles
  const { data: existingBundles } = useQuery({
    queryKey: ['product-bundles', productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_bundles')
        .select(`
          *,
          bundle_product:products!bundle_product_id(id, name, price, image_url)
        `)
        .eq('main_product_id', productId)
        ;
      
      if (error) throw error;
      return data;
    },
  });

  // Add bundle mutation
  const addBundleMutation = useMutation({
    mutationFn: async (bundleData: typeof newBundle) => {
      const { error } = await supabase
        .from('product_bundles')
        .insert({
          main_product_id: productId,
          bundle_product_id: bundleData.bundle_product_id,
          quantity: bundleData.quantity,
          discount_percentage: bundleData.discount_percentage
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Product bundle added successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['product-bundles', productId] });
      setNewBundle({ bundle_product_id: '', quantity: 1, discount_percentage: 0 });
      onBundlesChange?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add product bundle",
        variant: "destructive",
      });
    },
  });

  // Remove bundle mutation
  const removeBundleMutation = useMutation({
    mutationFn: async (bundleId: string) => {
      const { error } = await supabase
        .from('product_bundles')
        .delete()
        .eq('id', bundleId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Product bundle removed successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['product-bundles', productId] });
      onBundlesChange?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove product bundle",
        variant: "destructive",
      });
    },
  });

  const handleAddBundle = () => {
    if (!newBundle.bundle_product_id) {
      toast({
        title: "Error",
        description: "Please select a product to bundle",
        variant: "destructive",
      });
      return;
    }
    addBundleMutation.mutate(newBundle);
  };

  const handleRemoveBundle = (bundleId: string) => {
    removeBundleMutation.mutate(bundleId);
  };

  const calculateDiscountedPrice = (originalPrice: number, discountPercentage: number) => {
    return originalPrice * (1 - discountPercentage / 100);
  };

  return (
    <Card className="admin-card border-admin-border">
      <CardHeader>
        <CardTitle className="text-admin-foreground flex items-center gap-2">
          <Package className="h-5 w-5" />
          Product Bundles
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add New Bundle */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-medium text-admin-foreground">Add Bundle Product</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-admin-foreground">Product</Label>
              <Select
                value={newBundle.bundle_product_id}
                onValueChange={(value) => setNewBundle({ ...newBundle, bundle_product_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {availableProducts?.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} - ${product.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-admin-foreground">Quantity</Label>
              <Input
                type="number"
                min="1"
                value={newBundle.quantity}
                onChange={(e) => setNewBundle({ ...newBundle, quantity: parseInt(e.target.value) || 1 })}
              />
            </div>
            
            <div>
              <Label className="text-admin-foreground">Discount %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={newBundle.discount_percentage}
                onChange={(e) => setNewBundle({ ...newBundle, discount_percentage: parseFloat(e.target.value) || 0 })}
              />
            </div>
            
            <div className="self-end">
              <Button
                onClick={handleAddBundle}
                disabled={addBundleMutation.isPending}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                {addBundleMutation.isPending ? "Adding..." : "Add"}
              </Button>
            </div>
          </div>
        </div>

        {/* Existing Bundles */}
        <div className="space-y-3">
          <h4 className="font-medium text-admin-foreground">Current Bundles</h4>
          {existingBundles && existingBundles.length > 0 ? (
            <div className="space-y-3">
              {existingBundles.map((bundle) => (
                <div key={bundle.id} className="flex items-center justify-between p-3 border border-admin-border rounded">
                  <div className="flex items-center gap-3">
                    {bundle.bundle_product.image_url && (
                      <img
                        src={bundle.bundle_product.image_url}
                        alt={bundle.bundle_product.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                    )}
                    <div>
                      <h5 className="font-medium text-admin-foreground">
                        {bundle.bundle_product.name}
                      </h5>
                      <div className="flex items-center gap-2 text-sm text-admin-muted-foreground">
                        <span>Qty: {bundle.quantity}</span>
                        {bundle.discount_percentage > 0 && (
                          <>
                            <span>•</span>
                            <Badge variant="secondary">
                              {bundle.discount_percentage}% off
                            </Badge>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-admin-muted-foreground line-through">
                          ${bundle.bundle_product.price.toFixed(2)}
                        </span>
                        <span className="text-admin-foreground font-medium">
                          ${calculateDiscountedPrice(bundle.bundle_product.price, bundle.discount_percentage).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveBundle(bundle.id)}
                    disabled={removeBundleMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-admin-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-admin-foreground">No bundles created yet</p>
              <p className="text-sm">Add products to create bundles with this item</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductBundleManager;
